import * as React from 'react';
import { Layer, Source } from 'react-map-gl/mapbox';
import { Feature, FeatureCollection, Polygon, MultiPolygon, Point, LineString } from 'geojson';
import buffer from '@turf/buffer';
import convex from '@turf/convex';
import { featureCollection, point, lineString } from '@turf/helpers';

import { useGlobalState } from '../contexts/state/GlobalStateContext';
import { calculateGrids, getStructureCentroid } from '../util/GridUtils';

const GRID_BUFFER_RADIUS = 100; // meters

const GridsLayer = () => {
  const globalState = useGlobalState();

  const grids = React.useMemo(() =>
    calculateGrids(globalState.structures),
    [globalState.structures]
  );

  const getJSON = React.useMemo((): FeatureCollection => {
    const features: Feature<Polygon | MultiPolygon>[] = [];

    for (const grid of grids) {
      if (grid.structures.length === 0) continue;

      // Collect all structure centroids in the grid
      const coords: [number, number][] = grid.structures.map(structure => {
        const centroid = getStructureCentroid(structure);
        return [centroid.lng, centroid.lat];
      });

      let toBuffer: Feature<Point | LineString | Polygon>;

      if (coords.length === 1) {
        // Single point - buffer will create a circle
        toBuffer = point(coords[0]);
      } else if (coords.length === 2) {
        // Two points - create a line to buffer
        toBuffer = lineString(coords);
      } else {
        // 3+ points - create convex hull
        const points = featureCollection(coords.map(c => point(c)));
        const hull = convex(points);
        if (!hull) {
          // Convex hull failed (e.g., collinear points) - fall back to line
          toBuffer = lineString(coords);
        } else {
          toBuffer = hull;
        }
      }

      const buffered = buffer(toBuffer, GRID_BUFFER_RADIUS / 1000, {
        units: 'kilometers',
        steps: 32
      });

      if (buffered) {
        buffered.properties = { label: `Grid ${grid.number}` };
        features.push(buffered as Feature<Polygon | MultiPolygon>);
      }
    }

    return {
      type: 'FeatureCollection',
      features
    };
  }, [grids]);

  return (
    <Source type="geojson" data={getJSON}>
      <Layer
        id="grids"
        type="line"
        paint={{
          'line-color': 'rgba(255, 255, 255, 0.8)',
          'line-width': 2,
          'line-dasharray': [2, 4]
        }}
        layout={{
          'line-cap': 'round',
          'line-join': 'round'
        }}
      />
      <Layer
        id="grids-label"
        type="symbol"
        layout={{
          'symbol-placement': 'line-center',
          'text-field': ['get', 'label'],
          'text-size': 12,
          'text-font': ['Open Sans Regular', 'Arial Unicode MS Regular']
        }}
        paint={{
          'text-color': 'rgba(255, 255, 255, 0.8)',
          'text-halo-color': '#000000',
          'text-halo-width': 1
        }}
      />
    </Source>
  );
};

export { GridsLayer };
